/**
 * Which screen the app shows, as a function rather than a chain of early returns in JSX.
 *
 * Extracted because one of #14's acceptance criteria is a *negative* — "the scoring screen
 * is reachable only when signed in" — and a negative about routing is exactly the thing
 * that rots silently. Buried in `main.tsx` it could only be checked by driving a browser;
 * here it is a dozen lines and a test.
 */
import type { AuthUser } from "@sportsos/engine";

/**
 * `"loading"` is a real state: on boot nobody knows yet, and guessing flashes a screen.
 *
 * `"unreachable"` is the other one, and it is not the same as signed out. The only way to
 * know who is signed in is to ask the server (ADR-0008), so a boot with no signal cannot
 * answer the question — and answering it *wrongly*, as `null`, is what locked a scorer out
 * of a match in his hand. See `screenFor`.
 *
 * `"guest"` is #43's "score without an account" (PRD ID-6). It is a *choice*, not a failure:
 * the scorer has said he does not want an account yet, and the app scores the match on this
 * device. Distinct from `null` because the answer to "may he reach the pad" differs.
 */
export type Session = "loading" | "unreachable" | "guest" | AuthUser | null;

/**
 * Where the scorer has navigated to. Not where he ends up — that is what this decides.
 *
 * A flat union with no payload, even though `match_detail` is about *a* match: which match
 * is held beside this in `App`, and threading it through here would make `screenFor` take
 * an argument it never reads and the test matrix below a cross-product of ids.
 */
export type Destination =
  | "home"
  /**
   * `/signin` and `/signup`, which are addresses now (ADR-0014).
   *
   * This reverses the reasoning `route.ts` used to carry — that the sign-in screen is a
   * *consequence* of having no session and never somewhere the scorer chose to be. That was
   * right while nothing could link to it. The front door needs a link target, and "sign in"
   * and "sign up" are different promises to a stranger, so they are two destinations.
   *
   * Reversed only that far: `screenFor` still outranks the address, so asking for `/signin`
   * with a valid session lands in the app, not on a form. A destination is a request, not an
   * instruction.
   */
  | "sign_in"
  | "sign_up"
  | "setup"
  | "scoring"
  | "match_detail"
  | "profile"
  | "settings"
  | "tournaments"
  | "tournament_new"
  | "tournament_detail"
  | "ai_assistant"
  | "join_team"
  | "player_stats";

export type Screen =
  | "loading"
  | "sign_in"
  | "sign_up"
  | "home"
  | "setup"
  | "scoring"
  | "match_detail"
  | "profile"
  | "settings"
  | "tournaments"
  | "tournament_new"
  | "tournament_detail"
  | "ai_assistant"
  | "join_team"
  | "player_stats";

/**
 * What a guest may reach without an account.
 *
 * Scoring, and the screens on the way to it. Everything else — the match list on the server,
 * a profile, a tournament — is *about* an account, and offering an empty version of it would
 * be a worse answer than asking for the account. The sign-in screen a guest is sent to keeps
 * its way back (`onBack` in `SignIn`), so this is an upsell rather than a trap.
 */
const GUEST_DESTINATIONS: ReadonlySet<Destination> = new Set<Destination>([
  "home",
  "setup",
  "scoring",
]);

/**
 * Resolve session and intent into the screen to render.
 *
 * The session outranks everything, which is the whole gate: no destination reaches the pad
 * while `session` is null, so there is no combination of navigation and stale state that
 * shows a signed-out scorer a match.
 *
 * A `"scoring"` destination with no draft falls back to home rather than rendering an empty
 * pad. That happens after a reload of a device with no match on it, and a blank scoring
 * screen with no match behind it is worse than the home screen.
 *
 * ## The one thing the gate does not outrank
 *
 * A match **already on this device**, when the server could not be reached to ask who is
 * signed in. Found by the browser drive in #16: with the API unreachable, a reload dumped the
 * scorer onto the sign-in screen mid-innings — his balls safe in IndexedDB, his cookie
 * probably still valid, and no way to enter the next delivery. At a ground with no signal
 * that ends the match, which is precisely the failure ADR-0010 exists to prevent, and it is
 * worse than losing a ball because it loses every ball still to come.
 *
 * This does not weaken #14's gate. `hasDraft` means a match was created on this device, which
 * could only have happened while signed in or as a declared guest; a stranger's phone has no
 * local match and still reaches nothing. Nor does it grant anything beyond continuing that
 * innings — no list, no new match, no setup, no profile — because those genuinely need the
 * server.
 */
export function screenFor(
  session: Session,
  destination: Destination,
  hasDraft: boolean,
): Screen {
  if (session === "loading") return "loading";
  if (session === "unreachable") {
    return destination === "scoring" && hasDraft ? "scoring" : formFor(destination);
  }
  if (session === null) return formFor(destination);
  if (session === "guest" && !GUEST_DESTINATIONS.has(destination)) return formFor(destination);
  // A session outranks the address, so someone who is already signed in and asks for a
  // sign-in form is answered with the thing the form leads to. The alternative is showing a
  // returning scorer a password field he does not need, which is the whole problem the front
  // door exists to fix.
  if (destination === "sign_in" || destination === "sign_up") return "home";
  if (destination === "scoring") return hasDraft ? "scoring" : "home";
  return destination;
}

/**
 * Which form a person with no usable session is shown.
 *
 * Sign-up only when they asked for it. Everywhere else is sign-in, because arriving at
 * `/match/:id` or `/matches` without a session says nothing about whether they have an
 * account, and guessing "you are new here" at someone who is not is how a returning scorer
 * ends up registering a second one.
 */
const formFor = (destination: Destination): Screen =>
  destination === "sign_up" ? "sign_up" : "sign_in";

/**
 * Whether the bottom tab bar is rendered over this screen.
 *
 * A function and not a `className`, because the interesting half is the *negative*: the
 * scoring screen is "one screen, no navigation inside the loop", and a tab bar over the pad
 * would put three destinations a thumb's width from the run keys — during the exact three
 * hours the scorer cannot afford to leave. That is a rule about the product, and rules about
 * the product rot silently when they live in JSX. Here it is one line and a test.
 *
 * Setup and sign-in are excluded for a different reason. Both are flows with their own way
 * back ("‹ Home", and signing in), and offering a tab out of a half-entered squad would
 * discard twenty-two names to answer a curiosity. `tournament_new` is the same flow shape
 * and is excluded on the same grounds.
 */
export function showsTabBar(screen: Screen): boolean {
  switch (screen) {
    case "home":
    case "match_detail":
    case "profile":
    case "settings":
    case "tournaments":
    case "tournament_detail":
    case "ai_assistant":
    case "player_stats":
      return true;
    case "loading":
    case "sign_in":
    case "sign_up":
    case "setup":
    case "scoring":
    case "tournament_new":
    case "join_team":
      return false;
  }
}
